import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { useQueryParamsWithTime } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  DocumentsQuery,
  DocumentsQueryVariables,
} from './__generated__/useDocuments';
import { queryParams } from './queryParams';

export const Documents_document = gql`
  fragment Documents_document on Document {
    expirationDate
    id
    status
    uuid
    verificationTypes
    uploadedAt
    documentType
    player {
      id
      uuid
    }
    uploadedBy {
      ... on PlayerInitiator {
        player {
          id
          firstName
          lastName
          email
        }
      }
      ... on AgentInitiator {
        agent {
          firstName
          lastName
          email
        }
      }
    }
    verifiedAt
    verifiedBy {
      ... on PlayerInitiator {
        player {
          firstName
          lastName
          email
        }
      }
      ... on AgentInitiator {
        agent {
          firstName
          lastName
          email
        }
      }
    }
    deletedAt
    deletedBy {
      ... on PlayerInitiator {
        player {
          firstName
          lastName
          email
        }
      }
      ... on AgentInitiator {
        agent {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const DOCUMENTS_QUERY = gql`
  query Documents(
    $documentType: [DocumentType!]!
    $documentStatus: [DocumentStatus!]!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $orderBy: DocumentSearchOrder
    $desc: Boolean
    $from: OffsetDateTime
    $to: OffsetDateTime
  ) {
    viewer {
      id
      documents(
        documentType: $documentType
        documentStatus: $documentStatus
        after: $after
        before: $before
        first: $first
        last: $last
        orderBy: $orderBy
        desc: $desc
        from: $from
        to: $to
      ) {
        edges {
          node {
            ...Documents_document
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
  ${Documents_document}
`;

export default function useDocuments() {
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const documentType = useMemo(() => {
    if (query.documentType) {
      return Array.isArray(query.documentType)
        ? query.documentType
        : [query.documentType];
    }
    return [];
  }, [query.documentType]);

  const documentStatus = useMemo(() => {
    if (query.status) {
      return Array.isArray(query.status) ? query.status : [query.status];
    }
    return [];
  }, [query.status]);

  const [{ data, fetching }, refresh] = useQuery<
    DocumentsQuery,
    DocumentsQueryVariables
  >({
    query: DOCUMENTS_QUERY,
    variables: {
      ...mapVariables(query),
      documentStatus,
      documentType,
    },
  });

  const documents = data?.viewer.documents;

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, documents?.pageInfo),
    documents: documents?.edges?.map((edge) => edge?.node),
  };
}
